import React, { memo, useContext } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { onKeyDown, open } from '../../utils';
import Header from '../Header';
import styles from './styles.module.scss';
import { LocaleContext } from '../../Banner';
import useMediaQuery from '../../../../utils/useMediaQuery';

export const SlidePropTypes = {
  slider: PropTypes.shape({
    url: PropTypes.string,
    img: PropTypes.string,
    imgMobile: PropTypes.string,
    imgWebp: PropTypes.string,
    imgMobileWebp: PropTypes.string,
    mobileGradientColorTop: PropTypes.string,
    mobileGradientColorBottom: PropTypes.string,
    h1: PropTypes.string,
    h2: PropTypes.string,
    h3: PropTypes.string,
    headerTextAlignment: PropTypes.string,
    promoCode: PropTypes.string,
    promoCodeRegistrationAutopopulation: PropTypes.string,
    button: PropTypes.shape({ text: PropTypes.string }),
    buttonTitle: PropTypes.string,
    registrationSource: PropTypes.string,
    addOns: PropTypes.shape({ title: PropTypes.string, description: PropTypes.string }),
    tc: PropTypes.shape({ url: PropTypes.string, text: PropTypes.string }),
    tcTitle: PropTypes.string,
  }).isRequired,
  isMobile: PropTypes.bool.isRequired,
};

const Slide = ({ slider, isMobile }) => {
  const { url, tc, tcTitle, promoCode, promoCodeRegistrationAutopopulation, addOns } = slider;
  const { img, imgMobile } = slider;
  const {
    h1,
    h2,
    h3,
    headerTextAlignment,
    button,
    buttonTitle,
    registrationSource,
    mobileGradientColorTop,
    mobileGradientColorBottom,
  } = slider;
  const { dataJurisdiction } = useContext(LocaleContext);

  const isDesktopWidth = useMediaQuery('(min-width: 768px)');

  const displayMobileBannerGradient =
    !isDesktopWidth && !(mobileGradientColorTop === '#000000' && mobileGradientColorBottom === '#000000');

  const desktopHeaderTextAlignment = isDesktopWidth ? { 'text-align': `${headerTextAlignment}` } : {};

  const h1Style = desktopHeaderTextAlignment;

  const h3Style = {
    ...desktopHeaderTextAlignment,
    ...(displayMobileBannerGradient
      ? {
          background: `linear-gradient(transparent 18%, ${mobileGradientColorBottom}, ${mobileGradientColorTop})`,
          minHeight: '2.5rem',
        }
      : {}),
  };

  const headerStyle = {
    h1: h1Style,
    h3: dataJurisdiction === 'en-uk' || dataJurisdiction === 'en-ir' ? {} : h3Style,
  };

  return (
    <>
      <div
        data-jurisdiction={dataJurisdiction}
        className={cn(styles.slideBackground)}
        data-testid="slider"
        aria-label="Slide background"
        onKeyDown={onKeyDown(url)}
        onClick={() => open(url, registrationSource, promoCodeRegistrationAutopopulation)}
        role="button"
        style={{ backgroundImage: `url(${isMobile && imgMobile ? imgMobile : img})` }}
        tabIndex={0}
      />
      <Header h1={h1} h2={h2} h3={h3} promoCode={promoCode} style={headerStyle} />
      <div data-jurisdiction={dataJurisdiction} className={cn(styles.slideActions)}>
        {button && (
          <button
            className={cn(styles.slideButton)}
            data-testid="button"
            title={buttonTitle || undefined}
            onClick={() => open(url, registrationSource, promoCodeRegistrationAutopopulation)}
          >
            {button.text}
          </button>
        )}

        {addOns && addOns.title && (
          <>
            {addOns.title && <span className={cn(styles.slideTitleAdditional)}>{addOns.title}</span>}
            {addOns.description && <span className={cn(styles.slideDescAdditional)}>{addOns.description}</span>}
          </>
        )}

        {tc && tc.url && tc.text && (
          <a className={cn(styles.termsCondition)} data-testid="tc" title={tcTitle || undefined} href={tc.url}>
            <p>{tc.text}</p>
          </a>
        )}
      </div>
    </>
  );
};

Slide.propTypes = SlidePropTypes;

export default memo(Slide);
